import { Icon } from "@iconify/react";
import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import step1Image from "../../../assets/images/bulk/step1.png";
import step2Image from "../../../assets/images/bulk/step2.png";
import step3Image from "../../../assets/images/bulk/step3.png";
import Button from "../../../components/buttons/Button";
import ProgressBar from "../../../components/graphs/ProgressBar";
import BottomBar from "../../../components/misc/BottomBar";
import Dialog from "../../../components/misc/Dialog";
import ThreeDotLoader from "../../../components/misc/ThreeDotLoader";
import { SUPPORTED_EXTENSIONS } from "../../../config";
import { useError } from "../../../contexts/ErrorContext";
import Translations from "../../../lang/en/bulk.json";
import Minimal from "../../../layouts/Minimal";
import { useCaseManagement, useFileUpload } from "../hooks/useCaseManagement";
import BulkFileUpload from "./BulkFileUpload";
import { CaseUploadError, CaseUploadItem, FileStatus } from "./index.m";
import { CreateCaseError, CreateRevisionError } from "../types";

const StepImage = styled.img`
  max-width: 600px;
  max-height: 373px;
  width: auto;
`;

interface StepProps {
  title: string;
  description: string;
  image: string;
  tip?: string;
  notice?: string;
}

const StepBase: React.FC<StepProps> = ({
  title,
  description,
  image,
  tip,
  notice,
}) => {
  return (
    <Box width={"100%"} margin={"auto"}>
      <Stack direction={"row"} spacing={2} alignItems={"flex-start"}>
        <Stack
          sx={{
            width: "100%",
          }}
        >
          <Typography
            color={"success"}
            fontWeight={"light"}
            variant="h6"
            textAlign={"left"}
            sx={{ mt: 2 }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            textAlign={"left"}
            fontWeight="light"
            sx={{ mt: 1 }}
          >
            {description}
          </Typography>
          {notice && (
            <Typography
              mt={4}
              textAlign={"left"}
              variant="body2"
              sx={{
                color: "rgba(0, 0, 0, 0.56)",
                background: "rgba(0, 121, 107, 0.04)",
                borderRadius: 4,
                px: 3,
                py: 2,
              }}
            >
              {notice}
            </Typography>
          )}
        </Stack>
        <Box width={"100%"}>
          <StepImage src={image} alt={title} />
        </Box>
      </Stack>
      {tip && (
        <Stack
          direction={"row"}
          spacing={1}
          mt={2}
          width={800}
          alignItems={"center"}
        >
          <Typography
            textAlign={"left"}
            sx={{ color: "rgba(0,0,0, 0.56)" }}
            fontWeight={"bold"}
          >
            Tip:{" "}
          </Typography>
          <Typography
            textAlign={"left"}
            sx={{ color: "rgba(0,0,0, 0.56)" }}
            fontWeight={"light"}
          >
            {tip}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

const Wizard: React.FC = () => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const images = [step1Image, step2Image, step3Image];
  const [cases, setCases] = React.useState<CaseUploadItem[]>([]);
  const [errors, setErrors] = React.useState<CaseUploadError[]>([]);
  const [filesWithoutCase, setFilesWithoutCase] = React.useState<string[]>([]);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);

  const { createCase, createRevision } = useCaseManagement();
  const { uploadFiles } = useFileUpload();
  const { showError, showSuccess } = useError();
  const theme = useTheme();
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = React.useState(false);

  const navigate = useNavigate();

  const handleNavigateBack = () => {
    if (currentStep === images.length || cases.length > 0) {
      console.info("files not ready, going back to first step");
      setCases([]);
      setErrors([]);
      setFilesWithoutCase([]);
      setCurrentStep(0);
      return;
    }

    setCurrentStep(images.length);
  };

  const handleNavigateNext = async () => {
    if (currentStep === images.length) {
      await startUploading();
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, images.length));
  };

  const nextText = useMemo(() => {
    if (cases.length > 0) {
      return Translations.navigation.analyze;
    }
    if (currentStep < images.length - 1) {
      return Translations.navigation.continue;
    }
    if (currentStep === images.length) {
      return "";
    }
    return Translations.navigation.next;
  }, [currentStep, images.length, cases.length]);

  const prevText = useMemo(() => {
    if (cases.length > 0) {
      return Translations.navigation.cancel;
    }
    if (currentStep < images.length - 1) {
      return Translations.navigation.skip;
    }
    return Translations.navigation.previous;
  }, [currentStep, images.length, cases.length]);

  const goBack = () => {
    setCases([]);
    setErrors([]);
    setFilesWithoutCase([]);
    setIsDialogOpen(false);
  };

  const startUploading = async () => {
    setIsDialogOpen(false);
    setIsUploading(true);
    const maxCaseCreateThreads = 5;

    // Filter out cases with case-level errors
    const validCases = cases.filter((caseItem, index) => {
      const caseError = errors[index];
      return !caseError?.caseError;
    });

    const threads = [];
    for (let i = 0; i < validCases.length; i += maxCaseCreateThreads) {
      threads.push(validCases.slice(i, i + maxCaseCreateThreads));
    }

    for (const thread of threads) {
      await Promise.all(
        thread.map(async (caseItem) => {
          // Get the original index of this case
          const originalIndex = cases.indexOf(caseItem);
          const caseError = errors[originalIndex];
          try {
            const caseId = await createCase();
            const revisionId = await createRevision(caseId);

            // Filter out files with errors
            const validFiles = caseItem.files
              .filter((f) => {
                if (!caseError?.fileErrors) return true;
                return !caseError.fileErrors.some(
                  (fileError) => fileError.filename === f.file.name
                );
              })
              .map((f) => ({
                file: f.file,
                url: "",
              }));

            console.info("validFiles", validFiles);

            // Update status to uploading
            caseItem.status = "uploading";
            setCases((prev) =>
              prev.map((c) => (c.caseName === caseItem.caseName ? caseItem : c))
            );

            await uploadFiles(caseId, revisionId, validFiles);

            caseItem.progress = 100;

            setCases((prev) =>
              prev.map((c) => (c.caseName === caseItem.caseName ? caseItem : c))
            );

            caseItem.status = "uploaded";
            console.info(`Uploaded case: ${caseItem.caseName}`);
          } catch (err) {
            caseItem.status = "error";
            setErrors((prev) => {
              const copy = [...prev];
              copy[originalIndex] = {
                ...copy[originalIndex],
                caseError: Translations.upload.errors.uploadFailed,
              };
              return copy;
            });

            console.error(`Error uploading case: ${caseItem.caseName}`, err);
            if (err instanceof CreateCaseError) {
              showError(Translations.upload.errors.uploadFailed);
            } else if (err instanceof CreateRevisionError) {
              showError(Translations.upload.errors.uploadFailed);
            } else {
              console.error("Unexpected error during case upload", err);
              const message =
                err instanceof Error ? err.message : `An unexpected error occurred while uploading case: ${caseItem.caseName}`;
              showError(message);
            }
          }
        })
      );
    }

    setIsUploading(false);
    setIsCompleteDialogOpen(true);
    showSuccess(Translations.upload.success);
  };

  const downloadReport = () => {
    console.info("Downloading report...");
    // Here you would typically handle the report download logic
    // For now, we just log the data
    console.info("Errors:", errors);
  };

  const tableHeaderTexts = Translations.upload.filesTable.tableHeaderTexts;

  const onUploadChange = (
    newCases: CaseUploadItem[],
    newErrors: CaseUploadError[],
    newFilesWithoutCase: string[]
  ) => {
    setCases(newCases);
    setErrors(newErrors);
    setFilesWithoutCase(newFilesWithoutCase);

    console.info("Cases updated:", newCases);
    console.info("Errors updated:", newErrors);
    console.info("Files without case updated:", newFilesWithoutCase);

    // Check if there are any errors
    const hasErrors =
      newErrors.some(
        (error) =>
          error.caseError || (error.fileErrors && error.fileErrors.length > 0)
      ) || newFilesWithoutCase.length > 0;

    if (hasErrors) {
      setIsDialogOpen(true);
    }
  };

  const renderStatus = useCallback((status: FileStatus | "skipped") => {
    const statusWord =
      status === "skipped"
        ? Translations.upload.status.skipped || "Skipped"
        : Translations.upload.status[status];

    switch (status) {
      case "uploading":
        return <ThreeDotLoader label={statusWord} color={"#014361"} />;
      case "uploaded":
        return (
          <Typography color={"#00796B"} fontWeight={"bold"}>
            {statusWord}
          </Typography>
        );
      case "error":
        return (
          <Typography color={theme.palette.error.main} fontWeight={"bold"}>
            {statusWord}
          </Typography>
        );
      case "skipped":
        return (
          <Typography
            color={theme.palette.error.main}
            fontWeight={"light"}
            fontStyle={"italic"}
          >
            {statusWord}
          </Typography>
        );
      case "queued":
        return (
          <Typography color={"#014361"} fontWeight={"light"}>
            {statusWord}
          </Typography>
        );
    }
  }, []);

  return (
    <Minimal>
      <Stack
        mb={4}
        maxWidth={1200}
        spacing={2}
        textAlign="center"
        sx={{ position: "relative" }}
        width={"100%"}
        height={window.innerHeight - 100}
        mt={10}
      >
        {currentStep === images.length ? (
          <Stack>
            {cases.length === 0 && <BulkFileUpload onChange={onUploadChange} />}
            {cases.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: "5%",
                        backgroundColor: "#ffffff",
                        borderTopLeftRadius: 12,
                        borderBottomLeftRadius: 12,
                      }}
                    ></TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#ffffff", width: "40%" }}
                    >
                      {tableHeaderTexts.caseName}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#ffffff",
                        textAlign: "center",
                        width: "15%",
                      }}
                    >
                      {tableHeaderTexts.filesDetected}
                    </TableCell>
                    <TableCell
                      sx={{
                        // justifyItems: "flex-end",
                        textAlign: "right",
                        backgroundColor: "#ffffff",
                        borderTopRightRadius: 12,
                        borderBottomRightRadius: 12,
                        width: "75%",
                        pr: 6.5,
                      }}
                    >
                      {tableHeaderTexts.status}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cases.map((caseObj, index) => {
                    const caseError = errors[index];
                    const hasCaseError = caseError?.caseError;

                    // Calculate valid files count
                    const validFilesCount = caseObj.files.filter((f) => {
                      if (!caseError?.fileErrors) return true;
                      return !caseError.fileErrors.some(
                        (fileError) => fileError.filename === f.file.name
                      );
                    }).length;

                    // Determine the status to display
                    const displayStatus = hasCaseError
                      ? "skipped"
                      : caseObj.status;

                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <Icon
                            color="rgba(0, 121, 107, 0.12)"
                            icon="mdi:folder"
                            width="24"
                            height="24"
                          />
                        </TableCell>
                        <TableCell>
                          <Stack spacing={1} alignItems={"flex-start"}>
                            <Typography
                              color="#79747E"
                              fontWeight={"light"}
                              variant="body2"
                            >
                              {caseObj.caseName}
                            </Typography>
                            <ProgressBar
                              emptyColor="transparent"
                              filledColor="#2E7D32"
                              progress={caseObj.progress ?? 0}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          {validFilesCount}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            justifyItems: "flex-end",
                            textAlign: "right",
                            pr: 4,
                          }}
                        >
                          {renderStatus(displayStatus)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Stack>
        ) : (
          Translations.steps.map((step, index) => (
            <Box
              sx={{
                p: 2,
                visibility: currentStep === index ? "visible" : "hidden",
              }}
              height={"100%"}
              key={index}
              position={"absolute"}
              width={"100%"}
              top={0}
            >
              <StepBase
                notice={step.notice}
                title={step.title ?? ""}
                description={step.description}
                image={images[index]}
                tip={step.tip}
              />
            </Box>
          ))
        )}
      </Stack>
      <BottomBar
        buttons={[
          <Button
            disabled={isUploading}
            sx={{ minWidth: currentStep >= images.length - 1 ? 300 : "auto" }}
            onClick={handleNavigateBack}
            key={0}
            variant="text"
            color={"primary"}
          >
            <Stack direction={"row"} alignItems={"center"} gap={1}>
              {currentStep >= images.length - 1 ? <ArrowBack /> : null}
              <Typography>{prevText}</Typography>
            </Stack>
          </Button>,
          nextText ? (
            <Button
              disabled={isUploading}
              onClick={handleNavigateNext}
              key={0}
              sx={{ minWidth: currentStep >= images.length - 1 ? 300 : "auto" }}
              variant="green"
            >
              <Stack direction={"row"} alignItems={"center"} gap={1}>
                {currentStep >= images.length - 1 ? (
                  <Icon icon="mdi:check" width="24" height="24" />
                ) : (
                  <Icon icon="mdi:arrow-right" width="24" height="24" />
                )}
                <Typography>{nextText}</Typography>
              </Stack>
            </Button>
          ) : (
            <></>
          ),
        ]}
      />
      <Dialog
        backgroundColor={"#FAFAFA"}
        showClose={false}
        maxWidth={window.innerWidth < 600 ? "sm" : "md"}
        onClose={() => setIsDialogOpen(false)}
        open={isDialogOpen}
      >
        <Stack justifyContent={"center"} spacing={2} alignItems={"center"}>
          <Icon
            color="#FFB300"
            icon="material-symbols:warning-outline"
            width="24"
            height="24"
          />
          <Typography
            textAlign={"center"}
            variant="body1"
            color="#404943"
            fontSize={14}
          >
            <Box>{Translations.upload.errorsDialog.title}</Box>
            <Box>{Translations.upload.errorsDialog.title2}</Box>
          </Typography>

          <Typography
            textAlign={"center"}
            variant="body1"
            fontWeight={"bold"}
            fontSize={14}
          >
            {Translations.upload.errorsDialog.bold.replace(
              "{supportedFormats}",
              SUPPORTED_EXTENSIONS.map((f) => f.toUpperCase()).join(" ")
            )}
          </Typography>
          <Stack
            maxHeight={"100%"}
            overflow={"auto"}
            width={"100%"}
            spacing={1}
            sx={{
              borderRadius: 2,
              p: 2,
              backgroundColor: "#ffffff",
            }}
          >
            {errors
              .map((caseError, caseIndex) => ({ caseError, caseIndex }))
              .filter(
                ({ caseError }) =>
                  caseError.caseError ||
                  (caseError.fileErrors && caseError.fileErrors.length > 0)
              )
              .map(({ caseError, caseIndex }, idx, arr) => (
                <Box
                  key={caseIndex}
                  sx={{
                    mb: 1,
                  }}
                >
                  <Typography fontWeight={"bold"} variant="body1">
                    {cases[caseIndex].caseName}
                  </Typography>

                  {/* Display case-level error if exists */}
                  {caseError.caseError && (
                    <Stack
                      direction={"row"}
                      mt={1}
                      spacing={2}
                      alignItems={"center"}
                    >
                      <Icon
                        icon="material-symbols:close"
                        width="24"
                        height="24"
                        color={"#FF7043"}
                      />
                      <Typography variant="body2" color="error" fontSize={14}>
                        {caseError.caseError}
                      </Typography>
                    </Stack>
                  )}

                  {/* Display file-level errors */}
                  {caseError.fileErrors?.map((fileError, index) => (
                    <Stack
                      direction={"row"}
                      mt={1}
                      key={index}
                      spacing={2}
                      alignItems={"center"}
                    >
                      <Tooltip title={fileError.errors.join(", ")}>
                        <Icon
                          icon="material-symbols:close"
                          width="24"
                          height="24"
                          color={"#FF7043"}
                        />
                      </Tooltip>

                      <Tooltip title={fileError.errors.join(", ")}>
                        <Typography variant="body2" color="black" fontSize={14}>
                          {fileError.filename}
                        </Typography>
                      </Tooltip>
                    </Stack>
                  ))}
                  {idx < arr.length - 1 && <Divider sx={{ my: 2 }} />}
                </Box>
              ))}
          </Stack>
          {filesWithoutCase.length > 0 && (
            <Tooltip
              title={filesWithoutCase.map((f) => (
                <Box key={f}>{f}</Box>
              ))}
            >
              <Stack
                alignSelf={"flex-start"}
                direction={"row"}
                justifyContent={"flex-start"}
                alignItems={"center"}
                spacing={2}
                width={"100%"}
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Icon
                  icon="material-symbols:close"
                  width="24"
                  height="24"
                  color={"#FF7043"}
                />
                <Typography fontWeight={"light"} variant="body2">
                  {Translations.upload.errorsDialog.filesWithoutCase.replace(
                    "{filesCount}",
                    filesWithoutCase.length.toString()
                  )}
                </Typography>
              </Stack>
            </Tooltip>
          )}
        </Stack>
        <Stack
          mt={2}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Button
            variant="text"
            width={200}
            onClick={downloadReport}
            sx={{ color: theme.palette.purple.main }}
          >
            {Translations.upload.errorsDialog.buttons.downloadReport}
          </Button>
          <Stack direction={"row"} spacing={2} alignItems={"center"}>
            <Button
              variant="text"
              onClick={goBack}
              sx={{ color: theme.palette.primary.light }}
            >
              {Translations.upload.errorsDialog.buttons.back}
            </Button>
            <Button
              width={600}
              sx={{ maxWidth: 270 }}
              variant="green"
              onClick={startUploading}
            >
              {Translations.upload.errorsDialog.buttons.continue}
            </Button>
          </Stack>
        </Stack>
      </Dialog>
      <Dialog
        open={isCompleteDialogOpen}
        onClose={() => navigate("/cases")}
        backgroundColor={"#FAFAFA"}
        maxWidth={650}
        sx={{ height: 120 }}
        minHeight={120}
        closeText={Translations.upload.completeDialog.closeText}
      >
        <Stack justifyContent={"center"} spacing={2} alignItems={"center"}>
          <Typography variant="body1">
            {Translations.upload.completeDialog.description}
          </Typography>
        </Stack>
      </Dialog>
    </Minimal>
  );
};

export default Wizard;
